import axios from "axios";
import tokenService from "./tokenService";

// Prefer ``VITE_API_BASE_URL`` when set (explicit override); fall back to
// the absolute ``VITE_BACKEND_BASE_URL`` (SaaS builds + local dev, which
// now hits the backend directly — there is no Vite dev proxy); final
// fallback is same-origin ``/api`` — the deployed (PaaS/EKS) image carries
// no backend hostname and its nginx reverse-proxies /api to the BFF.
// Exported for callers that can't go through the axios instance — e.g.
// the SSE stream in useSubtaskEventStream, which needs `fetch` to attach
// an Authorization header (EventSource can't send one).
export const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_BACKEND_BASE_URL ||
  "/api";

// ── Force-logout listener bus ─────────────────────
const logoutListeners = new Set();

export function onForceLogout(cb) {
  logoutListeners.add(cb);
  return () => logoutListeners.delete(cb);
}

function forceLogout() {
  tokenService.clear();
  sessionStorage.clear();
  localStorage.clear();
  logoutListeners.forEach((cb) => cb());
}

// ── Axios instance ────────────────────────────────
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// ── Request interceptor: attach access token ──────
// Async so it can PROACTIVELY refresh an already-expired access token
// BEFORE the request goes out — that turns the common "expired token"
// case from 401 → silent-refresh → retry (which logs a 401 in the console)
// into a single clean request. The reactive 401 interceptor below stays as
// the safety net for a server-side expiry the client clock missed.
api.interceptors.request.use(async (config) => {
  if (config._skipAuth) return config;
  await ensureFreshToken();
  const token = tokenService.getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor: silent refresh on 401 ───
let refreshPromise = null;

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    // Only attempt refresh once per request, and skip for auth-free calls
    if (
      error.response?.status !== 401 ||
      original._retry ||
      original._skipAuth
    ) {
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      await refreshAuthToken();
      // Retry with the fresh token
      original.headers.Authorization = `Bearer ${tokenService.getAccessToken()}`;
      return api(original);
    } catch {
      forceLogout();
      return Promise.reject(
        new Error("Session expired. Please sign in again."),
      );
    }
  },
);

/**
 * Shared, mutex-protected access-token refresh. Reused by BOTH the axios
 * 401 interceptor above AND the SSE stream (``consumeEventStream``), so a
 * token expiry on either path triggers exactly ONE refresh — never two
 * racing ``/auth/refresh`` calls. Rejects when there's no refresh token
 * or the refresh call fails (the caller then force-logs-out).
 */
let lastRefreshAt = 0;

export function refreshAuthToken() {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken()
      .then((t) => {
        lastRefreshAt = Date.now();
        return t;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * Proactively refresh an EXPIRED access token so the next request doesn't
 * 401. No-op unless there's a refresh token AND the access token decodes as
 * expired AND we didn't just refresh (the 15s guard bounds a clock-skew
 * loop that would otherwise refresh on every request). Any refresh failure
 * is swallowed — the request then goes out and the reactive 401 interceptor
 * force-logs-out. Shared by the axios request interceptor and the SSE
 * stream so both paths avoid the reactive 401 round-trip.
 */
export async function ensureFreshToken() {
  if (
    !tokenService.getRefreshToken() ||
    !tokenService.isAccessTokenExpired() ||
    Date.now() - lastRefreshAt < 15000
  ) {
    return;
  }
  try {
    await refreshAuthToken();
  } catch {
    /* refresh impossible — reactive 401 path handles the force-logout */
  }
}

// Exposed so non-axios callers (the SSE stream) can drop the session the
// same way the interceptor does when a refresh is impossible.
export { forceLogout };

async function refreshAccessToken() {
  const refreshToken = tokenService.getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token");

  const { data } = await axios.post(
    `${BASE_URL}/auth/refresh`,
    { refresh_token: refreshToken },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );

  tokenService.setTokens(data);
  return data.access_token;
}

// Raw axios instance — exported so RTK Query's baseQuery can reuse the
// same interceptor stack (auth header + silent 401 refresh).
export { api as apiClient };

// ── Convenience helpers (return data directly) ────
export default {
  get: (path, opts) => api.get(path, opts).then((r) => r.data),
  post: (path, body, opts) => api.post(path, body, opts).then((r) => r.data),
  put: (path, body, opts) => api.put(path, body, opts).then((r) => r.data),
  patch: (path, body, opts) => api.patch(path, body, opts).then((r) => r.data),
  del: (path, opts) => api.delete(path, opts).then((r) => r.data),

  // For calls that should skip auth (login, register, set-password
  // landing — the URL token IS the credential).
  noAuth: {
    get: (path, opts) =>
      api.get(path, { ...(opts || {}), _skipAuth: true }).then((r) => r.data),
    post: (path, body) =>
      api.post(path, body, { _skipAuth: true }).then((r) => r.data),
  },
};
